import React, { useContext, useMemo } from 'react'

import { Link } from 'react-router-dom'

import {
  Box,
  Health,
  List,
  ListItem,
  makeAppStyles,
  ProviderLogo,
  Typography,
} from '@percept/mui'

import { OpenInNew } from '@percept/mui/icons'

import { CampaignSetupView } from './CampaignSetupView'

import { cardLoadingStyles, TimeseriesContext, getInsightCategoryLabel, OrgUnitContext } from '@percept/app-components'

import {
  ProviderCategoryScoreTimeseriesDatum,
  TimeseriesHealthDatum,
  useOrgUnitCategoryScores,
} from '@percept/hooks'

import { BubbleChartDatum } from './BubbleChart'

import { flatten, get, intersection, sortBy } from 'lodash-es'

import { CampaignSetupContext } from './context'

import { CAMPAIGN_SETUP_GRANULARITY, getPreviousTimeseriesDateRange, getTimeseriesDateRange, useReportAvailability } from './lib'

import { providerLabelMap, reportProvidersV2 } from '@percept/constants'

import { HealthType, InsightCategory, PlatformUnit, ReportProviderV2, ReportSeries } from '@percept/types'


type InsightCategoryDatum = {
  provider: ReportProviderV2
  category: InsightCategory
  health: number | null
  reportSeriesId: string
}

const getOrgUnitReportSeriesByProvider = (
  orgUnit: PlatformUnit
): Partial<Record<ReportProviderV2, ReportSeries>> => (
  (orgUnit.report_series || []).reduce( (acc, s) => {
    acc[s.provider.slug] = s
    return acc
  }, {} as Partial<Record<ReportProviderV2, ReportSeries>>)
)

const responseAdapter = (
  response: ProviderCategoryScoreTimeseriesDatum[],
  orgUnit: PlatformUnit
): BubbleChartDatum<InsightCategoryDatum, ReportProviderV2>[] => {
  const overallDataByCategory: Partial<Record<InsightCategory, TimeseriesHealthDatum[]>> = {}

  for( const d of response ){
    for( const t of Object.keys(d.timeseriesByCategory) as InsightCategory[] ){
      if( t !== 'OVERALL' as InsightCategory ){
        overallDataByCategory[t] = [
          ...overallDataByCategory[t] || [],
          ...d.timeseriesByCategory[t],
        ]
      }
    }
  }

  const allData = flatten(
    Object.values(overallDataByCategory)
  )

  const latestTimestamp = Math.max(...allData.map( d => d.start.getTime()))

  const reportSeriesMap = getOrgUnitReportSeriesByProvider(orgUnit)

  return response.reduce( (acc, item) => {
    const { provider } = item
    const reportSeries = reportSeriesMap[provider]
    const categories = Object.keys(item.timeseriesByCategory) as InsightCategory[]
    categories.forEach( category => {
      if( category !== 'OVERALL' as InsightCategory ){
        item.timeseriesByCategory[category].forEach( d => {
          if( reportSeries && d.start.getTime() === latestTimestamp && d.health !== null ){
            acc.push({
              key: provider,
              column: provider,
              health: d.health,
              data: {
                provider,
                reportSeriesId: reportSeries.id,
                category,
                health: d.health,
              },
            })
          }
        })
      }
    })
    return acc
  }, [] as BubbleChartDatum<InsightCategoryDatum, ReportProviderV2>[])
}

const useStyles = makeAppStyles( theme => ({
  ...cardLoadingStyles,
  providerLink: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
    fontWeight: theme.typography.fontWeightBold,
    transition: theme.transitions.create('color'),
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  providerLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
  },
  linkIcon: {
    marginRight: theme.spacing(0.75),
  },
  orgLink: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: theme.spacing(2),
  },
  health: {
    marginLeft: 'auto',
    paddingLeft: theme.spacing(2),
  },
}))


export const SingleOrgCampaignSetup = () => {
  const orgUnit = useContext(OrgUnitContext)

  const [urlState] = useContext(CampaignSetupContext)
  
  const availability = useReportAvailability()
  
  const minDate = availability && availability.start
  const maxDate = availability && availability.end

  const referenceDate = (
    urlState.referenceDate === 'latest' ?
      maxDate :
      new Date(urlState.referenceDate)
  )

  const granularity = CAMPAIGN_SETUP_GRANULARITY

  const dateRange = getTimeseriesDateRange(referenceDate, granularity)

  const categoryScores = useOrgUnitCategoryScores({
    org_unit_id: orgUnit.id,
    ...dateRange,
    granularity,
    enabled: !!dateRange.start,
    retry: false,
  })

  const previousCategoryScores = useOrgUnitCategoryScores({
    org_unit_id: orgUnit.id,
    ...getPreviousTimeseriesDateRange(referenceDate, granularity),
    granularity,
    enabled: !!dateRange.start,
    retry: false,
  })

  const reportSeriesMap = getOrgUnitReportSeriesByProvider(orgUnit)
  
  const bubbleChartData = useMemo(() => {
    return responseAdapter(categoryScores.data || [], orgUnit)
  }, [categoryScores.data, orgUnit])

  const previousBubbleChartData = useMemo(() => {
    return responseAdapter(previousCategoryScores.data || [], orgUnit)
  }, [previousCategoryScores.data, orgUnit])
  

  const providers = intersection(
    reportProvidersV2,
    bubbleChartData.map( d => d.column )
  )

  const overallScoresByProvider = (categoryScores.data || []).reduce( (acc, d) => {
    const data = get(d.timeseriesByCategory, 'OVERALL', 0)
    const health = data && data.length ? data[data.length - 1].health : null
    acc[d.provider] = health
    return acc
  }, {} as Record<ReportProviderV2, HealthType>)

  const classes = useStyles()

  return (
    <TimeseriesContext.Provider value={{
      ...dateRange,
      minDate,
      maxDate,
      granularity,
    }}>
      <CampaignSetupView
        cardTitle='Category Scores by Platform'
        tooltipContent={'Distribution of setup scores by pillar and ad platform.'}
        isError={categoryScores.isError}
        isLoading={categoryScores.isLoading}
        isRefetching={categoryScores.isRefetching}
        dataSignal={!!categoryScores.data}
        columns={
          providers.map( provider => ({
            key: provider,
            label: (
              <Link
                className={classes.providerLink}
                to={`/series/${get(reportSeriesMap[provider], 'id')}`}>
                {/* <ProviderLogo provider={provider} size={2} /> */}
                <OpenInNew fontSize='small' className={classes.linkIcon} />
                { providerLabelMap[provider] }
                <Health
                  value={overallScoresByProvider[provider]}
                  color='inherit'
                  animate={false} />
              </Link>
            ),
          }))
        }
        data={bubbleChartData}
        previousData={previousBubbleChartData}
        datumKey='category'
        getPopoverContent={({ key, data }) => (
          <>
          <Box px={2} mt={2}>
            <Typography variant='h6' className={classes.providerLabel}>
              { providerLabelMap[key] }
            </Typography>
            {/* <ProviderLogo provider={key} size={1.35} /> */}
          </Box>
          <List>
            { sortBy(data, 'health').map( d => (
              <ListItem
                key={d.category}
                component={Link}
                className={classes.orgLink}
                button
                to={{
                  pathname: `/series/${d.reportSeriesId}`,
                  search: `report=latest&category=${d.category}`,
                }}>
                <OpenInNew fontSize='small' className={classes.icon} />
                { getInsightCategoryLabel(d.category) }
                <Health
                  value={d.health}
                  color='inherit'
                  className={classes.health} />
              </ListItem>
            ))}
          </List>
          </>
        )} />
    </TimeseriesContext.Provider>
  )
}
